<template>
  <div class="cart-line-product-quantity">
    <button
        class="minus"
        @click="onMinus"
        :disabled="inputValue <= 0"
    >
      <img src="@/assets/images/minus.svg" alt="minus" />
    </button>

    <input
        ref="quantityInput"
        type="number"
        class="quantity-input"
        min="0"
        :max="effectiveMaxAvailable"
        :value="inputValue"
        @input="handleInput"
        @blur="handleBlur"
        @keyup.enter="handleEnter"
        :disabled="isUpdateProcessing"
    />

    <button
        class="plus"
        @click="add"
        :disabled="inputValue >= effectiveMaxAvailable"
    >
      <img src="@/assets/images/plus.svg" alt="plus" />
    </button>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { showConfirm, showWarning } from '@/lib/swal2'

export default {
  name: 'CartLineProductQuantity',
  props: {
    limit: { type: Number },
    productId: { type: String, required: true },
    sizeId: { type: Number, default: null },
    quantity: { type: Number, required: true },
    itemNotAvailable: { type: Number, default: 0 },
    balance: { type: Number, default: null },
  },
  data() {
    return {
      inputValue: this.quantity,
      debounceTimer: null,
      isUpdating: false,
      skipNextWatch: false,
      isUpdateProcessing: false,
    }
  },
  computed: {
    // Используем balance только если он меньше itemNotAvailable
    effectiveMaxAvailable() {
      // Если товара нет в наличии, лимит 0
      if (this.itemNotAvailable <= 0) {
        return 0;
      }

      // Если balance передан и он строго меньше реального остатка, используем balance как лимит
      if (this.balance !== null && this.balance > 0 && this.balance < this.itemNotAvailable) {
        return this.balance;
      }

      // Во всех остальных случаях используем itemNotAvailable
      return this.itemNotAvailable;
    },
    // Флаг, используется ли balance в качестве лимита (для передачи в стор)
    isBalanceLimit() {
      return this.balance !== null &&
          this.balance > 0 &&
          this.balance < this.itemNotAvailable;
    },
    unitLabel() {
      return 'шт.';
    }
  },
  watch: {
    quantity(newVal) {
      // Если мы только что скорректировали значение, пропускаем это обновление
      if (this.skipNextWatch) {
        this.skipNextWatch = false;
        return;
      }

      this.inputValue = newVal;
    },
    itemNotAvailable(newVal) {
      console.debug('WATCH: itemNotAvailable changed to', newVal);
      // Проверяем, не превышает ли текущее значение новый лимит
      this.checkCurrentValueAgainstLimit();
    },
    balance(newVal) {
      console.debug('WATCH: balance changed to', newVal);
      // Проверяем, не превышает ли текущее значение новый лимит
      this.checkCurrentValueAgainstLimit();
    }
  },
  mounted() {
    console.debug("CartLineProductQuantity mounted.",
        "Limit:", this.limit,
        "Balance:", this.balance,
        "ItemNotAvailable:", this.itemNotAvailable,
        "EffectiveMax:", this.effectiveMaxAvailable
    );
  },
  methods: {
    ...mapActions('cart/dealer', [
      'addProductUpdateLine',
      'removeProductUpdateLine',
      'updateProductQuantity'
    ]),

    // Новый метод для проверки текущего значения при изменении лимитов
    checkCurrentValueAgainstLimit() {
      if (this.effectiveMaxAvailable > 0 && this.inputValue > this.effectiveMaxAvailable) {
        this.skipNextWatch = true;
        this.inputValue = this.effectiveMaxAvailable;
        this.updateToMax();
      }
    },

    add() {
      if (this.effectiveMaxAvailable > 0 && this.inputValue >= this.effectiveMaxAvailable) {
        // Показываем предупреждение через swal (как было)
        showWarning(
            'Ограничение количества',
            `Максимально доступно ${this.effectiveMaxAvailable} ${this.unitLabel}`
        );
        return;
      }

      this.addProductUpdateLine({
        productId: this.productId,
        sizeId: this.sizeId
      });
    },

    async onMinus() {
      if (this.quantity === 1) {
        const { isConfirmed } = await showConfirm(
            'Подтвердите действие',
            'Удалить товар из корзины?'
        );
        if (!isConfirmed) return;
      }

      await this.removeProductUpdateLine({
        productId: this.productId,
        sizeId: this.sizeId,
        quantity: 1,
        completely: this.quantity === 1
      });
    },

    handleInput(event) {
      // Очищаем предыдущий таймер
      clearTimeout(this.debounceTimer);

      const inputValue = event.target.value;

      // Если поле пустое, оставляем как есть
      if (inputValue === '') {
        this.inputValue = '';
        return;
      }

      const numValue = Number(inputValue);

      // Если не число, игнорируем
      if (Number.isNaN(numValue)) {
        return;
      }

      // Если отрицательное, ставим 0
      if (numValue < 0) {
        this.inputValue = 0;
        this.debounceTimer = setTimeout(() => this.commitUpdate(0), 500);
        return;
      }

      // Проверяем превышение лимита
      if (this.effectiveMaxAvailable > 0 && numValue > this.effectiveMaxAvailable) {
        this.skipNextWatch = true;
        this.inputValue = this.effectiveMaxAvailable;
        this.updateToMax();
        return;
      }

      // Для нормальных значений обновляем отображение и используем дебаунс
      this.inputValue = numValue;

      // Планируем обновление через 400 мс
      this.debounceTimer = setTimeout(() => {
        this.commitUpdate(numValue);
      }, 400);
    },

    async updateToMax() {
      if (this.isUpdating) return;

      this.isUpdating = true;

      try {
        const maxAvailable = this.effectiveMaxAvailable;
        this.isUpdateProcessing = true;

        await this.updateProductQuantity({
          productId: this.productId,
          sizeId: this.sizeId,
          quantity: maxAvailable,
          isMax: true,
          availableQuantity: this.effectiveMaxAvailable,
          isBalance: this.isBalanceLimit, // Флаг для стора
        });

      } catch (error) {
        this.skipNextWatch = false;
        this.inputValue = this.quantity;
      } finally {
        this.isUpdating = false;
        this.isUpdateProcessing = false;
        this.inputValue = this.effectiveMaxAvailable;
      }
    },

    handleBlur() {
      // Очищаем таймер при blur
      clearTimeout(this.debounceTimer);

      // Если поле пустое, восстанавливаем
      if (this.inputValue === '') {
        this.inputValue = this.quantity;
        return;
      }

      const numValue = Number(this.inputValue);

      if (Number.isNaN(numValue)) {
        this.inputValue = this.quantity;
        return;
      }

      // Проверяем превышение максимума при blur
      if (this.effectiveMaxAvailable > 0 && numValue > this.effectiveMaxAvailable) {
        this.skipNextWatch = true;
        this.inputValue = this.effectiveMaxAvailable;
        this.updateToMax();
        return;
      }

      this.commitUpdate(numValue);
    },

    handleEnter() {
      // Очищаем таймер при Enter
      clearTimeout(this.debounceTimer);

      const numValue = Number(this.inputValue);

      if (Number.isNaN(numValue)) {
        this.inputValue = this.quantity;
        return;
      }

      this.commitUpdate(numValue);
    },

    async commitUpdate(value) {
      if (this.isUpdating) return;

      // Дополнительная проверка на максимум
      if (this.effectiveMaxAvailable > 0 && value > this.effectiveMaxAvailable) {
        this.skipNextWatch = true;
        this.inputValue = this.effectiveMaxAvailable;
        await this.updateToMax();
        return;
      }

      // Если не изменилось
      if (value === this.quantity) return;

      // Проверка на удаление
      if (value === 0) {
        const { isConfirmed } = await showConfirm(
            'Подтвердите действие',
            'Удалить товар из корзины?'
        );
        if (!isConfirmed) {
          this.inputValue = this.quantity;
          return;
        }
      }

      this.isUpdating = true;

      try {
        await this.updateProductQuantity({
          productId: this.productId,
          sizeId: this.sizeId,
          quantity: value,
          isMax: false,
          // isBalance не передаем, так как isMax=false
        });
      } catch (error) {
        this.inputValue = this.quantity;
      } finally {
        this.isUpdateProcessing = false;
        this.isUpdating = false;
      }
    }
  }
}
</script>

<style scoped>
.cart-line-product-quantity {
  display: flex;
  align-items: center;
  gap: 5px;
}

.minus, .plus {
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.minus:disabled,
.plus:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-input {
  width: 50px;
  height: 25px;
  text-align: center;
  padding: 4px;
  box-sizing: border-box;
  -webkit-appearance: none;
  margin: 0;
  border: 1px solid #ccc;
  border-radius: 3px;
  transition: border-color 0.3s;
}

.quantity-input:focus {
  outline: none;
  border-color: #d93030;
}

.quantity-input:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.quantity-input::-webkit-outer-spin-button,
.quantity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.quantity-input[type=number] {
  -moz-appearance: textfield;
}

@media (max-width: 767px) {
  .cart-line-product-quantity {
    gap: 3px;
  }
  .minus, .plus {
    width: 25px;
    height: 25px;
  }
  .quantity-input {
    width: 45px;
  }
}
</style>
